import { useEffect, useState } from "react";
import Dashboard from "../../common/components/layout/Dashboard";
import {
  getPurchaseOrderDashboard,
  PurchaseOrderDashboardData,
} from "../../common/services/purchaseOrder.api";
import { numberToCurrency } from "../../common/helpers/currency.helpers";
import { useNavigate } from "react-router-dom";
import { useCompanyStore } from "@/common/states/store/Company";
import { PURCHASEORDER_STATUS } from "../../types/purchaseOrder.types";
import { HiArrowLeft } from "react-icons/hi";

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  approved: { bg: "#dcfce7", text: "#166534" },
  "pre-approved": { bg: "#fef9c3", text: "#854d0e" },
  pending: { bg: "#dbeafe", text: "#1e40af" },
  "pending-release": { bg: "#f3f4f6", text: "#374151" },
  rejected: { bg: "#fee2e2", text: "#991b1b" },
  canceled: { bg: "#f3f4f6", text: "#6b7280" },
};

const STATUS_LABEL: Record<string, string> = {
  [PURCHASEORDER_STATUS.APPROVED]: "Aprovada",
  [PURCHASEORDER_STATUS.PRE_APPROVED]: "Pré-Aprovada",
  [PURCHASEORDER_STATUS.PENDING]: "Pendente",
  [PURCHASEORDER_STATUS.REJECTED]: "Reprovada",
  [PURCHASEORDER_STATUS.CANCELED]: "Cancelada",
  [PURCHASEORDER_STATUS.PENDING_RELEASE]: "Lanç. Pendente",
};

function getMonthRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const start = new Date(y, m, 1);
  const end = new Date(y, m + 1, 0);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { startDate: fmt(start), endDate: fmt(end) };
}

function PurchaseOrderDashboardPage() {
  const navigate = useNavigate();
  const companyStore = useCompanyStore();
  const [data, setData] = useState<PurchaseOrderDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState(getMonthRange);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const result = await getPurchaseOrderDashboard(
        dateRange.startDate,
        dateRange.endDate
      );
      if (result.ok) {
        setData(result.data);
      }
      setIsLoading(false);
    })();
  }, [dateRange, companyStore]);

  return (
    <Dashboard>
      <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/compras/ordem")}
              className="p-2 rounded-lg hover:bg-gray-100 transition cursor-pointer"
              title="Voltar"
            >
              <HiArrowLeft className="text-xl text-gray-600" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">
              Dashboard — Ordem de Compras
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) =>
                setDateRange((prev) => ({
                  ...prev,
                  startDate: e.target.value,
                }))
              }
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[rgb(228,65,49)]"
            />
            <span className="text-gray-500">até</span>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) =>
                setDateRange((prev) => ({
                  ...prev,
                  endDate: e.target.value,
                }))
              }
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[rgb(228,65,49)]"
            />
          </div>
        </div>

        {isLoading && (
          <div
            className="flex min-h-[min(420px,calc(100vh-14rem))] w-full flex-col items-center justify-center gap-4 py-12"
            aria-busy="true"
            aria-live="polite"
          >
            <div
              className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-[rgb(228,65,49)]"
              role="status"
              aria-label="Carregando"
            />
            <p className="text-sm text-gray-500">Carregando dados…</p>
          </div>
        )}

        {!isLoading && data && (
          <div className="flex flex-col gap-6">
            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">
                  Total de Ordens
                </p>
                <p className="text-3xl font-bold text-gray-800">
                  {data.summary.totalOrders.toLocaleString("pt-BR")}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">
                  Valor Total
                </p>
                <p className="text-3xl font-bold text-[rgb(228,65,49)]">
                  {numberToCurrency(data.summary.totalValue)}
                </p>
              </div>
            </div>

            {/* Status breakdown */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Por Status
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {data.totalsByStatus.map((item) => {
                  const colors = STATUS_COLORS[item.status] ?? {
                    bg: "#f3f4f6",
                    text: "#374151",
                  };
                  return (
                    <div
                      key={item.status}
                      className="rounded-lg p-4 text-center"
                      style={{ backgroundColor: colors.bg }}
                    >
                      <p
                        className="text-xs font-semibold uppercase mb-2"
                        style={{ color: colors.text }}
                      >
                        {STATUS_LABEL[item.status] ?? item.status}
                      </p>
                      <p
                        className="text-2xl font-bold"
                        style={{ color: colors.text }}
                      >
                        {item.totalOrders.toLocaleString("pt-BR")}
                      </p>
                      <p
                        className="text-xs mt-1"
                        style={{ color: colors.text, opacity: 0.8 }}
                      >
                        {numberToCurrency(item.totalValue)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Rankings */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Ranking by Company */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Ranking por Filial
                </h2>
                {data.rankingByCompany.length === 0 ? (
                  <p className="text-sm text-gray-400">
                    Nenhum dado no período.
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <tbody>
                      {data.rankingByCompany.map((company, idx) => {
                        const pct =
                          data.summary.totalValue > 0
                            ? ((company.totalValue / data.summary.totalValue) * 100).toFixed(2)
                            : "0.0";
                        return (
                          <tr
                            key={company.companyId}
                            className={idx % 2 === 0 ? "bg-gray-50" : "bg-white"}
                          >
                            <td className="py-2 px-3 text-gray-500 w-8 text-right font-medium">
                              {idx + 1}.
                            </td>
                            <td className="py-2 px-3 text-gray-700 font-medium">
                              {company.companyName}
                            </td>
                            <td className="py-2 px-3 text-gray-500 text-right whitespace-nowrap">
                              {company.totalOrders} ordens
                            </td>
                            <td className="py-2 px-3 text-gray-700 text-right font-semibold whitespace-nowrap">
                              {numberToCurrency(company.totalValue)}
                            </td>
                            <td className="py-2 px-3 text-right whitespace-nowrap w-16">
                              <span className="inline-block bg-red-50 text-[rgb(228,65,49)] text-xs font-semibold px-2 py-0.5 rounded-full">
                                {pct}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Ranking by Category */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Ranking por Categoria
                </h2>
                {data.rankingByCategory.length === 0 ? (
                  <p className="text-sm text-gray-400">
                    Nenhum dado no período.
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <tbody>
                      {data.rankingByCategory.map((category, idx) => {
                        const pct =
                          data.summary.totalValue > 0
                            ? ((category.totalValue / data.summary.totalValue) * 100).toFixed(2)
                            : "0.0";
                        return (
                          <tr
                            key={category.categoryId}
                            className={idx % 2 === 0 ? "bg-gray-50" : "bg-white"}
                          >
                            <td className="py-2 px-3 text-gray-500 w-8 text-right font-medium">
                              {idx + 1}.
                            </td>
                            <td className="py-2 px-3 text-gray-700 font-medium">
                              {category.categoryName}
                            </td>
                            <td className="py-2 px-3 text-gray-500 text-right whitespace-nowrap">
                              {category.totalOrders} ordens
                            </td>
                            <td className="py-2 px-3 text-gray-700 text-right font-semibold whitespace-nowrap">
                              {numberToCurrency(category.totalValue)}
                            </td>
                            <td className="py-2 px-3 text-right whitespace-nowrap w-16">
                              <span className="inline-block bg-amber-50 text-amber-600 text-xs font-semibold px-2 py-0.5 rounded-full">
                                {pct}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Dashboard>
  );
}

export default PurchaseOrderDashboardPage;
